// src/hooks/useThemeTokens.ts
// Applies the admin-editable palette on top of the shipped CSS defaults.
// Tokens live in Firestore (settings/theme) and are cached in localStorage so
// the correct palette paints on first render instead of flashing the default.
import { useEffect } from "react";
import { getThemeTokens } from "../services/database";
import {
  ThemeTokens,
  ThemeMode,
  TOKEN_VARS,
  DEFAULT_TOKENS,
  FONT_SETS,
  DEFAULT_FONT_SET,
} from "../constants/themePresets";

const CACHE_KEY = "portfolio-theme-tokens-v1";
const STYLE_ID = "brand-theme-tokens";
const FONT_LINK_ID = "brand-theme-fonts";

/** Swap the Google Fonts stylesheet for the chosen pairing. */
const applyFontSet = (id?: string) => {
  const set = FONT_SETS.find((f) => f.id === (id || DEFAULT_FONT_SET));
  if (!set || typeof document === "undefined") return "";

  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (set.google) {
    const href = `https://fonts.googleapis.com/css2?family=${set.google}&display=swap`;
    if (existing) {
      if (existing.href !== href) existing.href = href;
    } else {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  } else if (existing) {
    existing.remove();
  }

  return `:root{--font-display:${set.display};--font-family:${set.body};--font-mono:${set.mono};}`;
};

const cssFor = (mode: ThemeMode, selector: string) => {
  const body = (Object.keys(TOKEN_VARS) as (keyof ThemeMode)[])
    .map((k) => `${TOKEN_VARS[k]}: ${mode[k]};`)
    .join("");
  return `${selector}{${body}}`;
};

/** Writes a <style> tag with the token overrides (both themes at once). */
export const applyThemeTokens = (tokens: ThemeTokens) => {
  if (typeof document === "undefined") return;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  const radius = tokens.radius ?? DEFAULT_TOKENS.radius;
  el.textContent = [
    cssFor(tokens.dark, ':root, [data-theme="dark"]'),
    cssFor(tokens.light, '[data-theme="light"]'),
    `:root{--radius:${radius}px;--radius-lg:${radius + 4}px;}`,
    applyFontSet(tokens.fontSet),
  ].join("");
};

export const readCachedTokens = (): ThemeTokens | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ThemeTokens) : null;
  } catch {
    return null;
  }
};

export const cacheTokens = (tokens: ThemeTokens) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(tokens));
  } catch {
    /* non-fatal */
  }
};

/** Call once near the app root. */
export const useThemeTokens = () => {
  useEffect(() => {
    const cached = readCachedTokens();
    if (cached) applyThemeTokens(cached);

    let cancelled = false;
    getThemeTokens().then((tokens) => {
      if (cancelled || !tokens) return;
      applyThemeTokens(tokens);
      cacheTokens(tokens);
    });

    return () => {
      cancelled = true;
    };
  }, []);
};
